import CloseIcon from '@mui/icons-material/Close';
import { css } from '@emotion/react';
import {
  alpha,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useAtom } from 'jotai';
import { FC, useEffect, useState } from 'react';
import { FooterButtons } from '../../components/Dialog/ConfirmDialog/FooterButtons';
import { ScheduleValue, SourcingList, SourcingSavedSearch } from '../../schemas/SourcingList.schema';
import { emptyDraft, hydrateSchedule, isScheduleValid } from './schedule/schedule';
import { ScheduleBlock } from './schedule/ScheduleBlock';
import { ScheduleSummaryBanner } from './schedule/ScheduleSummaryBanner';
import { useUpdateListSchedule } from './hooks/useSourcingListActions';
import { DIALOG_PX, dialogPaperSx } from './sourcing-dialog.styles';
import { refreshSettingsDialogAtom } from './state/SourcingListsState';

function isSavedSearch(list: SourcingList | SourcingSavedSearch): list is SourcingSavedSearch {
  return list.listType === 'active';
}

export const RefreshSettingsDialog: FC = () => {
  const { colors } = useTheme();
  const [list, setList] = useAtom(refreshSettingsDialogAtom);
  const mutation = useUpdateListSchedule();
  const [schedule, setSchedule] = useState<ScheduleValue>(null);

  useEffect(() => {
    // Default the segment control to "Scheduled" with an empty draft when the
    // list has no saved schedule — per Masha's UX pass, opening the dialog on a
    // fresh list should land you on the schedule editor, not Manual Only.
    if (list) {
      setSchedule(hydrateSchedule(list) ?? emptyDraft());
    }
  }, [list]);

  if (!list) {
    return null;
  }

  const saving = mutation.isPending;
  const savedSearch = isSavedSearch(list) ? list : null;
  const subjectLabel = savedSearch ? 'query' : 'enrichment';
  const canSave = !saving && isScheduleValid(schedule);
  const hasSavedSchedule = hydrateSchedule(list) !== null;
  const close = () => setList(null);

  const handleCancel = () => {
    if (saving) {
      return;
    }
    close();
  };

  const handleSave = () => {
    if (!canSave) {
      return;
    }
    mutation.mutate({ list, schedule }, { onSuccess: close });
  };

  return (
    <Dialog open onClose={handleCancel} maxWidth={false} slotProps={{ paper: { sx: dialogPaperSx } }}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 18,
          mb: 2,
          px: DIALOG_PX,
        }}
      >
        {savedSearch ? 'Refresh Settings' : 'Auto-Enrich Settings'}
        <IconButton size='small' aria-label='Close' onClick={handleCancel} disabled={saving}>
          <CloseIcon sx={{ fontSize: 20, color: colors.neutral[40] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '10px', px: DIALOG_PX }}>
        <ScheduleBlock
          value={schedule}
          onChange={setSchedule}
          disabled={saving}
          subjectLabel={savedSearch ? null : 'Auto-Enrich'}
          hasSavedSchedule={hasSavedSchedule}
        />

        {savedSearch?.query && (
          <div>
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: '4px' }}>
              Query
            </Typography>
            <div
              css={css`
                background-color: ${colors.primary[5]};
                border-radius: 4px;
                padding: 16px 12px;
              `}
            >
              <Typography
                variant='caption'
                color={alpha(colors.primary[100], 0.87)}
                sx={{ whiteSpace: 'pre-wrap', display: 'block' }}
              >
                {savedSearch.query}
              </Typography>
            </div>
          </div>
        )}

        <ScheduleSummaryBanner schedule={schedule} subjectLabel={subjectLabel} />
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-start', gap: '4px', py: 12, px: DIALOG_PX }}>
        <FooterButtons
          confirmButtonText='Save Changes'
          onConfirm={handleSave}
          onConfirmDisabled={!canSave}
          onCancel={handleCancel}
          loading={saving}
        />
      </DialogActions>
    </Dialog>
  );
};
